/* eslint-disable jsx-a11y/click-events-have-key-events */
import React, { Component } from 'react';
import axios from 'axios';
import cl from 'classnames';
import Demo from './SkeletonDemo';
import Constants from '../../Constants';
import { createWoosmapMap, WoosmapMapBoundingBox } from '../Map/drawOnMap';
import Button from '../Button/Button';
import ButtonGroup from '../Button/ButtonGroup';
import CountrySelect from '../Select/CountrySelect';
import { tr } from '../utils/locale';
import Input from '../Input/Input';
import markerIcon from '../../images/marker.png';
import AddressAutocomplete from '../Woosmap/AddressAutocomplete';

const languages = [
    { value: 'fr', label: 'French' },
    { value: 'gb', label: 'English' },
    { value: 'es', label: 'Spanish' },
    { value: 'it', label: 'Italian' },
    { value: 'de', label: 'German' },
    { value: 'nl', label: 'Dutch' },
];

export default class AddressDemo extends Component {
    constructor(props) {
        super(props);
        const lang = (typeof window === 'object' && (navigator.language || navigator.userLanguage)) || 'gb';
        const defaultLang = languages.find((item) => lang.startsWith(item.value));

        this.state = {
            response: null,
            error: null,
            input: '',
            countries: [],
            language: (defaultLang || languages[1]).value,
            usecase: 'autocomplete',
            geometry: false,
            geocodeInput: '',
            showDetails: false,
        };
        this.map = null;
        this.marker = null;
        this.mounted = true;
        this.viewport = null;
        this.requestUrl = 'https://api.woosmap.com/address/autocomplete/json';
        this.requestDetailsUrl = 'https://api.woosmap.com/address/details/json';
        this.requestGeocodeUrl = 'https://api.woosmap.com/address/geocode/json';
        this.inputRef = React.createRef();
        this.addressAutocompleteRef = React.createRef();
        this.demoRef = React.createRef();
    }

    componentDidMount() {
        this.displayMap();
    }

    componentWillUnmount() {
        this.mounted = false;
    }

    onAutocompleteInputChangeCb = (input) => {
        this.setState(
            {
                error: null,
                input,
            },
            this.requestAddress
        );
    };

    requestAddress = () => {
        const { usecase } = this.state;
        axios
            .get(usecase === 'geocode' ? this.requestGeocodeUrl : this.requestUrl, {
                params: this.getRequestparams(),
            })
            .then((response) => {
                if (this.mounted) {
                    const state = {
                        showDetails: false,
                        response: response.data,
                        error: null,
                    };
                    if (usecase === 'geocode') {
                        // eslint-disable-next-line prefer-destructuring
                        state.selectedAddress = response.data.results[0];
                    }
                    this.setState(state, this.displayOnMap);
                }
            })
            .catch((error) => {
                const errorResult = (error && error.response && error.response.data) || 'Unhandled error';
                this.setState({ error: errorResult });
            });
    };

    requestAddressDetails = (result) => {
        const { language, geometry } = this.state;
        const params = {
            public_id: result.id,
            key: Constants.woosmapKey,
            language,
        };
        if (geometry) {
            params.fields = 'geometry';
        }
        axios
            .get(this.requestDetailsUrl, {
                params,
            })
            .then((response) => {
                if (this.mounted) {
                    this.setState(
                        {
                            error: null,
                            showDetails: true,
                            response: response.data.result,
                            selectedAddress: response.data.result,
                        },
                        this.displayOnMap
                    );
                }
            })
            .catch((error) => {
                const errorResult = (error && error.response && error.response.data) || 'Unhandled error';
                this.setState({ error: errorResult });
            });
    };

    getRequestparams = () => {
        const { input, geocodeInput, language, countries, showDetails, usecase, geometry } = this.state;
        if (showDetails) {
            const detailsParam = {
                key: Constants.woosmapKey,
            };
            if (geometry) {
                detailsParam.fields = 'geometry';
            }
            return detailsParam;
        }
        const params = {
            key: Constants.woosmapKey,
            language,
        };
        if (countries && countries.length > 0) {
            params.components = countries.map((item) => `country:${item.value}`).join('|');
        }
        if (usecase === 'geocode') {
            params.address = geocodeInput;
        } else {
            params.input = input;
        }
        if (geometry) {
            params.fields = 'geometry';
        }
        return params;
    };

    displayOnMap = () => {
        const { selectedAddress } = this.state;

        if (this.map && selectedAddress?.geometry.location) {
            if (this.marker) {
                this.marker.setMap(null);
                this.marker = null;
            }

            if (this.overlay) {
                this.overlay.setMap(null);
                this.overlay = null;
            }

            this.marker = new window.woosmap.map.Marker({
                icon: { url: markerIcon, scaledSize: { height: 46, width: 30 } },
                position: selectedAddress.geometry.location,
                map: this.map,
            });

            if (selectedAddress.geometry.viewport) {
                const { northeast, southwest } = selectedAddress.geometry.viewport;
                const bounds = {
                    north: northeast.lat,
                    east: northeast.lng,
                    south: southwest.lat,
                    west: southwest.lng,
                };
                this.overlay = new WoosmapMapBoundingBox(bounds);
                this.overlay.setMap(this.map);
                this.map.fitBounds(bounds, { top: 70, bottom: 70, left: 70, right: 70 });
            } else {
                this.map.setCenter(selectedAddress.geometry.location);
                this.map.setZoom(10);
            }
        }
    };

    selectAddress = (address) => {
        this.setState(
            {
                error: null,
                selectedAddress: address,
            },
            this.requestAddress
        );
    };

    renderHeaderFilters = () => {
        const { language, countries, usecase, geocodeInput } = this.state;
        let components = null;
        if (countries && countries.length > 0) {
            components = countries.map((item) => `country:${item.value}`).join('|');
        }

        const useCaseChoice = {
            key: 'usecase',
            label: tr('Use case'),
            component: (
                <ButtonGroup isLight key="usecase">
                    <Button
                        type="group"
                        key="autocomplete"
                        active={usecase === 'autocomplete'}
                        onClick={() => {
                            this.setState({ usecase: 'autocomplete', selectedAddress: null, showDetails: false });
                        }}
                        label={tr('Autocomplete')}
                    />
                    <Button
                        type="group"
                        key="geocode"
                        active={usecase === 'geocode'}
                        onClick={() => {
                            this.setState({ usecase: 'geocode', selectedAddress: null, showDetails: false });
                        }}
                        label={tr('Geocode')}
                    />
                </ButtonGroup>
            ),
        };

        const inputFilter = {
            label: tr('Type in an address '),
            component:
                usecase === 'autocomplete' ? (
                    <AddressAutocomplete
                        inputRef={this.inputRef}
                        key="addressAutocomplete"
                        ref={this.addressAutocompleteRef}
                        apiOrder={['address']}
                        woosmapKey={Constants.woosmapKey}
                        placeholder="Start to type in an address"
                        defaultLocality={this.defaultOrigin}
                        language={language}
                        componentsAddress={components}
                        callback={this.requestAddressDetails}
                        inputChangeCb={this.onAutocompleteInputChangeCb}
                    />
                ) : (
                    <form className="flex fullw mbi" key="addressInput" onSubmit={this.requestAddress}>
                        <Input
                            value={geocodeInput}
                            autocomplete={false}
                            placeholder={tr('Type in a full address and hit Go')}
                            onChange={(e) => this.setState({ geocodeInput: e.target.value })}
                        />
                        <Button label={tr('Go')} onClick={this.requestAddress} />
                    </form>
                ),
        };
        return [useCaseChoice, inputFilter];
    };

    renderFooterFilters = () => {
        const { language, geometry } = this.state;
        const filterTypes = {
            label: tr('Filter by'),
            component: (
                <div className="demo__input-container--inline" key="country">
                    <CountrySelect
                        isMulti
                        placeholder={tr('Country')}
                        filter={['fr', 'gb', 'sp', 'it', 'de', 'nl']}
                        onChange={(items) => this.setState({ countries: items }, this.requestAddress)}
                    />
                </div>
            ),
        };
        const filterGeometry = {
            label: false,
            key: 'geometry',
            component: (
                <Input
                    label={tr('Geometry only')}
                    type="checkbox"
                    checked={geometry}
                    onChange={(e) => {
                        this.setState({ geometry: e.target.checked });
                    }}
                />
            ),
        };

        const filterCountries = {
            label: tr('Language'),
            component: (
                <ButtonGroup className="language" isLight key="language">
                    {languages.map((item) => (
                        <Button
                            type="group"
                            key={item.value}
                            active={language === item.value}
                            onClick={() => {
                                this.setState({ language: item.value }, this.requestAddress);
                            }}
                        >
                            <span className={cl('flag', `flag-${item.value}`)} title={item.label} />
                        </Button>
                    ))}
                </ButtonGroup>
            ),
        };
        return [filterGeometry, filterTypes, filterCountries];
    };

    renderAddress = () => {
        const { selectedAddress, geometry } = this.state;
        if (geometry) {
            return null;
        }
        let addressComponents = [];
        const hasComponents = selectedAddress && selectedAddress.address_components;
        if (hasComponents) {
            addressComponents = selectedAddress.address_components;
        }
        let shippingAddress = '';
        let shippingAddress2 = '';
        let postcode = '';
        let locality = '';
        let state = '';
        let country = '';
        addressComponents.forEach((component) => {
            const [componentType] = component.types;
            switch (componentType) {
                case 'street_number': {
                    shippingAddress = `${component.long_name} ${shippingAddress}`;
                    break;
                }
                case 'route': {
                    shippingAddress += component.short_name;
                    break;
                }
                case 'postal_code': {
                    postcode = `${component.long_name}${postcode}`;
                    break;
                }
                case 'postal_code_suffix': {
                    postcode = `${postcode}-${component.long_name}`;
                    break;
                }
                case 'locality':
                    locality = component.long_name;
                    break;

                case 'state': {
                    state = component.long_name;
                    break;
                }
                case 'administrative_area_level_1': {
                    state = component.long_name;
                    break;
                }
                case 'country': {
                    country = component.long_name;
                    break;
                }
                case 'premise': {
                    shippingAddress2 = component.long_name;
                    break;
                }
                default:
                    break;
            }
        });

        return (
            <div className="demo--address__formcontainer">
                {!hasComponents && (
                    // eslint-disable-next-line jsx-a11y/interactive-supports-focus
                    <div role="button" className="overlay" />
                )}
                <Input
                    label="Apartment, unit, suite, or floor #"
                    placeholder="Apartment, unit, suite, or floor #"
                    value={shippingAddress2}
                    onChange={() => {}}
                />
                <Input
                    label="Street number, street name"
                    placeholder="Street number, street name"
                    value={shippingAddress}
                    onChange={() => {}}
                />
                <Input label="City" value={locality} placeholder="City" onChange={() => {}} />
                <Input
                    className="fullw"
                    label="State/Province"
                    placeholder="State/Province"
                    value={state}
                    onChange={() => {}}
                />
                <Input
                    className="fullw"
                    label="Postal code"
                    placeholder="Postal code"
                    value={postcode}
                    onChange={() => {}}
                />
                <Input label="Country/Region" placeholder="Country/Region" value={country} onChange={() => {}} />
            </div>
        );
    };

    displayMap = () => {
        if (this.timeoutMap) {
            clearTimeout(this.timeoutMap);
        }

        if (window.woosmap && window.woosmap.map && this.demoRef.current) {
            window.woosmap.map.config.setApiKey(Constants.woosmapKey);
            this.map = createWoosmapMap(this.demoRef.current.getMap());
        } else {
            this.timeoutMap = setTimeout(this.displayMap, 300);
        }
    };

    render() {
        const { response, error, showDetails, usecase } = this.state;
        let request = this.requestGeocodeUrl;
        if (usecase === 'autocomplete') {
            if (showDetails) {
                request = this.requestDetailsUrl;
            } else {
                request = this.requestUrl;
            }
        }
        return (
            <Demo
                noheader
                id="address-demo"
                className="demo--address"
                footerFilters={this.renderFooterFilters()}
                headerFilters={this.renderHeaderFilters()}
                request={request}
                params={this.getRequestparams()}
                referer="http://localhost/"
                response={error || response}
                result={this.renderAddress()}
                ref={this.demoRef}
                withMap
            />
        );
    }
}
